import { useMemo } from 'react';
import { current, original } from '@reduxjs/toolkit';
import { castDraft, createStore, PayloadAction, WritableDraft } from '@direct-frontend/modules-store';
import { deepClone, deepEqual, Dictionary, get, keys, Optional, Primitive, values } from '@direct-frontend/stdlib';

import { BaseFields } from '../fields/types';
import { setFieldValueAtPath } from '../fields/utils/setFieldValueAtPath';
import { stringifyFieldIdPath } from '../fields/utils/stringifyFieldIdPath';
import { AllValidatorTypes, BaseSubmitError, BaseValidationError, ValidatorType } from '../types';

import { generateArrayItemId } from './utils/arrayItemId';
import { getDefaultFieldState } from './utils/getDefaultFieldState';
import { getFieldPathById } from './utils/getFieldPathByFieldId';
import { getFieldsContext } from './utils/getFieldsContext';
import { getFieldsStateForValue } from './utils/getFieldsStateForValue';
import { storeManager } from './storeManager';
import {
    AsyncActionsMeta,
    ErrorsMap,
    FieldState,
    FieldValidationMeta,
    FormSliceState,
    FormState,
    FormStore,
    FormValidationMeta,
    StartedReaction,
} from './types';
import { setFieldModifiedAt, setParentFieldsModifiedAt } from './utils/modifiedAtUtils';
import { setParentsStateByKey } from './utils/stateByKey';
import { setParentsValueByKey } from './utils/valueByKey';
import { generateNewFormInitializationId } from './utils/formInitializationId';

/** Тип данных одной формы */
type FormData = FormSliceState['forms'][string];

/**
 * Создает новый уникальный ключ инициализации для формы и устанавливает маппинг formId -> formInitializationId
 *
 * @param state Draft состояния формы
 * @param formId Идентификатор формы
 * @returns Новый уникальный ключ инициализации
 */
function setNewFormInitializationId(state: WritableDraft<FormSliceState>, formId: string): string {
    const newFormInitializationId = generateNewFormInitializationId(formId);
    state.formIdToInitializationId[formId] = newFormInitializationId;
    return newFormInitializationId;
}

/** Возвращает true, если в объекте нет ни одной ошибки  */
function isValid(errorsMap: ErrorsMap<BaseValidationError>) {
    return errorsMap.onChange.length === 0 && errorsMap.onBlur.length === 0 && errorsMap.onSubmit.length === 0;
}

/** Возвращает true, если в метаданных валидации есть хотя бы 1 промис */
function isValidating(validationMeta: FieldValidationMeta) {
    return AllValidatorTypes.some(validatorType => {
        const promises = validationMeta?.[validatorType].promises ?? [];
        return promises.length > 0;
    });
}

/** Возвращает true, если выполняются асинхронные экшены */
function isAsyncActionsRunning(asyncActionsMeta: Dictionary<unknown>) {
    return keys(asyncActionsMeta).length > 0;
}

/** Обновляет статус isTouched у поля, всех его родителей и дочерних полей */
function setFieldIsTouched(form: WritableDraft<FormData>, fieldId: string) {
    function setFieldAndChildrenTouched(fieldId: string) {
        const fieldState = form.fieldsState[fieldId];
        if (!fieldState || fieldState.isTouched) return;

        fieldState.isTouched = true;

        if ('fields' in fieldState) {
            for (const nestedFieldId of values(fieldState.fields)) {
                setFieldAndChildrenTouched(nestedFieldId);
            }
        }
        if ('order' in fieldState) {
            for (const arrayItemId of fieldState.order) {
                setFieldAndChildrenTouched(arrayItemId);
            }
        }
    }

    function setParentFieldsTouched(fieldId: string) {
        const fieldState = form.fieldsState[fieldId];
        if (!fieldState) return;

        fieldState.isTouched = true;

        if (fieldState.parentId) {
            setParentFieldsTouched(fieldState.parentId);
        }
    }

    // Помечаем всю форму touched
    if (!form.state.isTouched) {
        form.state.isTouched = true;
    }

    // Помечаем все внутренние поля touched
    setFieldAndChildrenTouched(fieldId);
    // Помечаем все родительские поля touched
    const fieldState = form.fieldsState[fieldId];
    if (fieldState?.parentId) {
        setParentFieldsTouched(fieldState.parentId);
    }
}

/**
 * Удаляет стейт и контекст поля и всех дочерних
 *
 * @param form Данные слайса формы
 * @param fieldId Id поля
 * @param ignoreRootField стоит ли игнорировать корневое поле
 * используется для сохранения стейта корневого поля, напр. для сохранения valuesByKey при переключения union-поля
 * @returns
 */
function removeFieldAndChildrenStateAndContext(
    form: WritableDraft<FormData>,
    fieldId: string,
    ignoreRootField = false
) {
    const fieldState = form.fieldsState[fieldId];
    if (!fieldState) return;

    if (!ignoreRootField) {
        delete form.fieldsState[fieldId];
        delete form.context.fieldsContext[fieldId];
    }

    if ('fields' in fieldState) {
        for (const nestedFieldId of values(fieldState.fields)) {
            removeFieldAndChildrenStateAndContext(form, nestedFieldId);
        }
    }
    if ('order' in fieldState) {
        for (const arrayItemId of fieldState.order) {
            removeFieldAndChildrenStateAndContext(form, arrayItemId);
        }
    }
}

/** Создает инициализированный стейт формы */
function initializeForm({
    fields,
    formValue,
    formContext,
    fieldsContextArgs,
    previousFieldsState = {},
    preserveOnlyArrayOrder = false,
}: {
    fields: BaseFields;
    formValue: Dictionary<unknown>;
    formContext: unknown;
    fieldsContextArgs: Dictionary<unknown>;
    previousFieldsState?: FormSliceState['forms'][string]['fieldsState'];
    preserveOnlyArrayOrder?: boolean;
}): FormData {
    const formValueCopy = deepClone(formValue);

    const fieldsState = getFieldsStateForValue({
        value: formValue,
        initialValue: formValue,
        formValue,
        fields,
        previousFieldsState,
        preserveOnlyArrayOrder,
    });
    const fieldsContext = getFieldsContext({ fields, fieldsContextArgs, fieldsState, formValue });

    return {
        value: formValueCopy,
        state: {
            isSubmitting: false,
            isAwaitingStartedActions: false,
            submitErrors: [],
            errorsMap: {
                onChange: [],
                onBlur: [],
                onSubmit: [],
            },
            isTouched: false,
            initialValue: castDraft(formValueCopy),
        },
        context: {
            formContext,
            fieldsContextArgs,
            fieldsContext,
        },
        formValidationMeta: {
            onChange: {},
            onBlur: {},
            onSubmit: {},
        },
        fieldsState,
        subscriptions: {},
        reactions: {
            areReactionsStarted: false,
            form: [],
            fields: {},
        },
        isUninitializing: false,
    };
}

function stopAndCleanupValidation(form: WritableDraft<FormData>) {
    const formValidationMeta = form.formValidationMeta;
    formValidationMeta.onChange.controller?.abort();
    formValidationMeta.onBlur.controller?.abort();
    formValidationMeta.onSubmit.controller?.abort();

    for (const key of keys(form.fieldsState)) {
        const fieldValidationMeta = form.fieldsState[key]?.validationMeta;

        if (fieldValidationMeta) {
            fieldValidationMeta.onChange.controller?.abort();
            fieldValidationMeta.onBlur.controller?.abort();
            fieldValidationMeta.onSubmit.controller?.abort();
        }
    }
}

/** Удаляет стейт и контекст для инстанса поля и всех дочерних */
function resetFieldAndChildren(
    form: WritableDraft<FormData>,
    {
        formFields,
        fieldId,
    }: {
        formFields: BaseFields;
        fieldId: string;
    }
) {
    // Запоминаем parentId поля, чтобы восстановить его после удаления
    const parentId = form.fieldsState[fieldId]?.parentId;

    const fieldPath = getFieldPathById(() => form.fieldsState, fieldId);

    // Удаляем стейт и контекст поля и всех дочерних
    removeFieldAndChildrenStateAndContext(
        form,
        fieldId,
        Boolean(form.fieldsState[fieldId] && 'valuesByKey' in form.fieldsState[fieldId])
    );

    // А затем создаем новый fieldsState по актуальному значению
    const parentFieldValue = get(form.value, fieldPath);
    const parentFieldInitialValue = get(form.state.initialValue, fieldPath);

    Object.assign(
        form.fieldsState,
        getFieldsStateForValue({
            value: parentFieldValue,
            initialValue: parentFieldInitialValue,
            fields: formFields,
            formValue: form.value,
            // Подкидываем старое значение, чтобы была возможность восстановить valuesByKey
            previousFieldsState: form.fieldsState,
            id: fieldId,
            parentId: parentId,
        })
    );

    const newContext = getFieldsContext({
        fields: formFields,
        fieldsContextArgs: form.context.fieldsContextArgs,
        fieldsState: form.fieldsState,
        formValue: form.value,
    });

    updateFieldsContext(form.context.fieldsContext, newContext);
}

/**
 * Частично обновляет контекст полей, изменяя только те поля, которые изменились
 *
 * @param currentContext Текущий контекст полей
 * @param newContext Новый контекст полей
 */
function updateFieldsContext(currentContext: Dictionary<unknown>, newContext: Dictionary<unknown>) {
    for (const fieldId of keys(newContext)) {
        const fieldContext = newContext[fieldId];
        if (!deepEqual(currentContext[fieldId], fieldContext)) {
            currentContext[fieldId] = fieldContext;
        }
    }

    for (const fieldId of keys(currentContext)) {
        if (!(fieldId in newContext)) {
            delete currentContext[fieldId];
        }
    }
}

/** Создает модульный стор, в котором хранится полное состояние формы */
function createFormStore(name: string): FormStore {
    const store = createStore(`@adv-frontend/form-kit/${name}`);
    const initialState: FormSliceState = {
        formIdToInitializationId: {},
        forms: {},
    };

    // Намеренно используем `unsafeCreateSlice`, чтобы иметь возможность хранить промисы и AbortController'ы в стейте
    const slice = store.unsafeCreateSlice({
        name: 'form',
        initialState,
        reducers: {
            initializeForm: (
                state,
                action: PayloadAction<{
                    formId: string;
                    fields: BaseFields;
                    fieldsContextArgs: Dictionary<unknown>;
                    formValue: Dictionary<unknown>;
                    formContext: unknown;
                }>
            ) => {
                const { formId, fields, fieldsContextArgs, formValue, formContext } = action.payload;

                const newFormInitializationId = setNewFormInitializationId(state, formId);

                state.forms[newFormInitializationId] = castDraft(
                    initializeForm({ fields, formValue, formContext, fieldsContextArgs })
                );
            },

            resetForm: (
                state,
                action: PayloadAction<{ formInitializationId: string; formId: string; fields: BaseFields }>
            ) => {
                const { formInitializationId, formId, fields } = action.payload;

                const form = state.forms[formInitializationId];

                if (!form || form.state.isSubmitting) {
                    return;
                }

                const newFormInitializationId = setNewFormInitializationId(state, formId);

                stopAndCleanupValidation(form);

                const fieldsState = getFieldsStateForValue({
                    value: form.state.initialValue,
                    initialValue: form.state.initialValue,
                    formValue: current(form.value),
                    fields,
                    // Не подкидываем старое значение, т.к. всю форму reset'им
                    previousFieldsState: {},
                });

                state.forms[newFormInitializationId] = {
                    value: form.state.initialValue,
                    state: {
                        isSubmitting: false,
                        isAwaitingStartedActions: false,
                        submitErrors: [],
                        errorsMap: {
                            onChange: [],
                            onBlur: [],
                            onSubmit: [],
                        },
                        initialValue: form.state.initialValue,
                        // Если вызван reset, то с формой взаимодействовали
                        isTouched: true,
                    },
                    formValidationMeta: {
                        onChange: {},
                        onBlur: {},
                        onSubmit: {},
                    },
                    fieldsState,
                    // Сохраняем контекст формы и её полей
                    context: {
                        formContext: form.context.formContext,
                        fieldsContextArgs: form.context.fieldsContextArgs,
                        // Получаем контекст полей заново, т.к. идентификаторы элементов массива изменились
                        fieldsContext: getFieldsContext({
                            fields,
                            fieldsContextArgs: form.context.fieldsContextArgs,
                            fieldsState,
                            formValue: form.value,
                        }),
                    },
                    // Сохраняем подписки, чтобы не почистить форму
                    subscriptions: form.subscriptions,
                    // Так как реакции не перезапускались, то сохраняем информацию о них
                    reactions: form.reactions,
                    isUninitializing: false,
                };
            },

            reinitializeForm: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    formId: string;
                    fields: BaseFields;
                    formValue: Dictionary<unknown>;
                    fieldsContextArgs?: Dictionary<unknown>;
                    formContext?: unknown;
                }>
            ) => {
                const { fields, fieldsContextArgs, formValue, formContext, formInitializationId, formId } =
                    action.payload;

                const form = state.forms[formInitializationId];
                if (!form) {
                    return;
                }

                stopAndCleanupValidation(form);

                const newFormInitializationId = setNewFormInitializationId(state, formId);
                state.forms[newFormInitializationId] = initializeForm({
                    fields,
                    formValue,
                    // Если контекст не передан, то используем текущий
                    formContext: formContext ?? form.context.formContext,
                    fieldsContextArgs: fieldsContextArgs ?? form.context.fieldsContextArgs ?? {},
                    previousFieldsState: form.fieldsState,
                    // Сохраняем только order массивов для стабильности ID элементов
                    preserveOnlyArrayOrder: true,
                });
            },

            cleanupForm: (state, action: PayloadAction<{ formInitializationId: string }>) => {
                delete state.forms[action.payload.formInitializationId];
            },

            setFormValue: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    formFields: BaseFields;
                    formValue: unknown;
                }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];

                if (!form) {
                    return;
                }

                const originalFormValue = original(form.value);

                form.value = castDraft(action.payload.formValue);

                for (const field of keys(action.payload.formFields)) {
                    setFieldModifiedAt({ form, fieldId: field, originalFormValue, currentFormValue: form.value });
                }
            },

            setFieldValue: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    fieldId: string;
                    fieldValue: unknown;
                    formFields: BaseFields;
                }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];

                if (!form) {
                    return;
                }

                const originalFormValue = original(form.value);
                const valuePath = getFieldPathById(() => form.fieldsState, action.payload.fieldId);

                form.value = castDraft(
                    setFieldValueAtPath(
                        // Из-за того, что значение обернуто в Immer Draft, TypeScript не может корректно вывести тип
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                        form.value as object,
                        valuePath,
                        action.payload.fieldValue
                    )
                );

                setFieldModifiedAt({
                    form,
                    fieldId: action.payload.fieldId,
                    originalFormValue,
                    currentFormValue: form.value,
                });
                setParentsValueByKey({
                    form,
                    fieldId: action.payload.fieldId,
                    formFields: action.payload.formFields,
                });
            },

            setParentsStateByKey: (
                state,
                action: PayloadAction<{ formInitializationId: string; fieldId: string; formFields: BaseFields }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];

                if (!form) {
                    return;
                }

                setParentsStateByKey({
                    form,
                    fieldId: action.payload.fieldId,
                    formFields: action.payload.formFields,
                });
            },

            setIsUninitializing: (
                state,
                action: PayloadAction<{ formInitializationId: string; isUninitializing: boolean }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];

                if (!form) {
                    return;
                }

                form.isUninitializing = action.payload.isUninitializing;
            },

            setFieldValueAndResetState: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    formFields: BaseFields;
                    fieldId: string;
                    fieldValue: unknown;
                }>
            ) => {
                const { fieldValue, formFields, fieldId } = action.payload;
                const form = state.forms[action.payload.formInitializationId];

                if (!form) {
                    return;
                }

                const originalFormValue = original(form.value);

                form.value = castDraft(
                    setFieldValueAtPath(
                        // Из-за того, что значение обернуто в Immer Draft, TypeScript не может корректно вывести тип
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                        form.value as object,
                        getFieldPathById(() => form.fieldsState, fieldId),
                        fieldValue
                    )
                );

                resetFieldAndChildren(form, {
                    formFields,
                    fieldId,
                });

                setFieldModifiedAt({ form, fieldId, originalFormValue, currentFormValue: form.value });
                setParentsValueByKey({
                    form,
                    fieldId,
                    formFields,
                });
                setParentsStateByKey({
                    form,
                    fieldId,
                    formFields,
                });
            },

            setValidationErrors: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    formValidationErrorsMap: ErrorsMap<BaseValidationError>;
                    formValidationMeta?: FormValidationMeta;
                    fieldsValidation: Dictionary<{
                        meta: FieldValidationMeta;
                        errorsMap: FieldState<unknown, BaseValidationError>['errorsMap'];
                    }>;
                }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];

                if (!form) {
                    return;
                }

                for (const fieldId of keys(action.payload.fieldsValidation)) {
                    const fieldState = form.fieldsState[fieldId];
                    const fieldValidationPayload = action.payload.fieldsValidation[fieldId];

                    if (!fieldState || !fieldValidationPayload) continue;

                    const previousFieldsValidationErrorsMap = fieldState.errorsMap;
                    const nextFieldsValidationErrorsMap = fieldValidationPayload.errorsMap;

                    // Сравниваем старые ошибки поля с новыми, чтобы не перезаписывать теми же самыми значениями
                    if (
                        !previousFieldsValidationErrorsMap ||
                        !deepEqual(nextFieldsValidationErrorsMap, original(previousFieldsValidationErrorsMap))
                    ) {
                        if (nextFieldsValidationErrorsMap) {
                            form.fieldsState[fieldId] = {
                                ...fieldState,
                                errorsMap: nextFieldsValidationErrorsMap,
                            };
                        }
                    }

                    const fieldValidationMeta =
                        fieldState.validationMeta ??
                        (fieldState.validationMeta = {
                            onChange: {},
                            onBlur: {},
                            onSubmit: {},
                        });

                    Object.assign(fieldValidationMeta.onChange, fieldValidationPayload.meta.onChange);
                    Object.assign(fieldValidationMeta.onBlur, fieldValidationPayload.meta.onBlur);
                    Object.assign(fieldValidationMeta.onSubmit, fieldValidationPayload.meta.onSubmit);
                }

                const previousFormValidationErrorsMap = form.state.errorsMap;
                const nextFormValidationErrorsMap = action.payload.formValidationErrorsMap;

                if (!deepEqual(nextFormValidationErrorsMap, original(previousFormValidationErrorsMap))) {
                    form.state.errorsMap = castDraft(nextFormValidationErrorsMap);
                }

                if (action.payload.formValidationMeta) {
                    form.formValidationMeta = action.payload.formValidationMeta;
                }
            },

            addFormValidationErrors: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    validatorType: ValidatorType;
                    validationErrors: BaseValidationError[];
                }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];
                if (!form) {
                    return;
                }

                form.state.errorsMap[action.payload.validatorType].push(...action.payload.validationErrors);
            },

            addFieldValidationErrors: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    fieldId: string;
                    validatorType: ValidatorType;
                    validationErrors: BaseValidationError[];
                }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];
                if (!form) {
                    return;
                }

                const fieldKey = action.payload.fieldId;

                const fieldValidationErrors =
                    form.fieldsState[fieldKey] ?? (form.fieldsState[fieldKey] = getDefaultFieldState());

                fieldValidationErrors.errorsMap[action.payload.validatorType].push(...action.payload.validationErrors);
            },

            addSubmitErrors: (
                state,
                action: PayloadAction<{ formInitializationId: string; errors: BaseSubmitError[] }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];
                if (!form) {
                    return;
                }

                form.state.submitErrors.push(...action.payload.errors);
            },

            subscribe: (state, action: PayloadAction<{ formInitializationId: string; subscriptionId: string }>) => {
                const form = state.forms[action.payload.formInitializationId];

                if (form) {
                    form.subscriptions[action.payload.subscriptionId] = true;
                }
            },

            unsubscribe: (
                state,
                action: PayloadAction<{ formInitializationId: string; formId: string; subscriptionId: string }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];

                if (!form) {
                    return;
                }

                delete form.subscriptions[action.payload.subscriptionId];

                // Если подписок больше не осталось, то нужно сбросить данные формы
                if (keys(form.subscriptions).length === 0) {
                    delete state.forms[action.payload.formInitializationId];
                    // Если не изменилось formInitializationId
                    if (state.formIdToInitializationId[action.payload.formId] === action.payload.formInitializationId) {
                        delete state.formIdToInitializationId[action.payload.formId];
                    }
                }
            },

            updateFormState: (
                state,
                action: PayloadAction<{ formInitializationId: string; state: Partial<FormState> }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];

                if (!form) {
                    return;
                }

                Object.assign(form.state, action.payload.state);
            },

            updateValidationMeta: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    formValidationMeta?: Partial<FormValidationMeta>;
                    fieldsValidationMeta?: Dictionary<Optional<Partial<FieldValidationMeta>>>;
                }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];
                if (!form) {
                    return;
                }

                if (action.payload.formValidationMeta) {
                    Object.assign(form.formValidationMeta, action.payload.formValidationMeta);
                }

                if (!action.payload.fieldsValidationMeta) {
                    return;
                }

                for (const fieldId of keys(action.payload.fieldsValidationMeta)) {
                    const fieldState = form.fieldsState[fieldId];

                    if (!fieldState) continue;

                    const fieldValidationMeta =
                        fieldState.validationMeta ??
                        (fieldState.validationMeta = {
                            onChange: {},
                            onBlur: {},
                            onSubmit: {},
                        });

                    if (action.payload.fieldsValidationMeta[fieldId]) {
                        Object.assign(
                            fieldValidationMeta.onChange,
                            action.payload.fieldsValidationMeta[fieldId]?.onChange
                        );
                        Object.assign(fieldValidationMeta.onBlur, action.payload.fieldsValidationMeta[fieldId]?.onBlur);
                        Object.assign(
                            fieldValidationMeta.onSubmit,
                            action.payload.fieldsValidationMeta[fieldId]?.onSubmit
                        );
                    }
                }
            },

            touch: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    fieldId: string;
                }>
            ) => {
                const { formInitializationId, fieldId } = action.payload;
                const form = state.forms[formInitializationId];

                if (!form) {
                    return;
                }

                setFieldIsTouched(form, fieldId);
            },

            insertArrayItems: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    formFields: BaseFields;
                    arrayId: string;
                    value: unknown;
                    insertIndex: number;
                }>
            ) => {
                const { formInitializationId, formFields, insertIndex, arrayId } = action.payload;
                const form = state.forms[formInitializationId];

                if (!form) {
                    return;
                }

                const originalFormValue = original(form.value);

                const arrayPath = getFieldPathById(() => form?.fieldsState ?? {}, arrayId);

                const arrayValue = get<unknown[]>(form.value, arrayPath);

                arrayValue?.splice(insertIndex, 0, action.payload.value);

                const arrayInitialValue = get<unknown[]>(form.state.initialValue, arrayPath);

                const arrayItemValue = arrayValue?.[insertIndex];
                const arrayItemInitialValue = arrayInitialValue?.[insertIndex];

                const arrayFieldState = form.fieldsState[arrayId];

                if (!arrayFieldState || !('order' in arrayFieldState)) {
                    return;
                }

                const itemId = stringifyFieldIdPath([arrayId, generateArrayItemId()]);

                arrayFieldState.order.splice(insertIndex, 0, itemId);

                const fieldsStateForInserted = getFieldsStateForValue({
                    value: arrayItemValue,
                    initialValue: arrayItemInitialValue,
                    fields: formFields,
                    formValue: form.value,
                    // Подкидываем старое значение, чтобы была возможность восстановить valuesByKey
                    previousFieldsState: form.fieldsState,
                    id: itemId,
                    parentId: arrayId,
                    fieldsState: form.fieldsState,
                });

                Object.assign(form.fieldsState, fieldsStateForInserted);

                const newContext = getFieldsContext({
                    fields: formFields,
                    fieldsContextArgs: form.context.fieldsContextArgs,
                    fieldsState: form.fieldsState,
                    formValue: form.value,
                });

                updateFieldsContext(form.context.fieldsContext, newContext);

                setFieldModifiedAt({ form, fieldId: itemId, originalFormValue, currentFormValue: form.value });
                setParentsValueByKey({
                    form,
                    formFields,
                    fieldId: arrayId,
                });
                setParentsStateByKey({
                    form,
                    formFields,
                    fieldId: arrayId,
                });
            },

            removeArrayItems: (
                state,
                action: PayloadAction<
                    | {
                          formInitializationId: string;
                          arrayId: string;
                          removeIndex: number;
                          formFields: BaseFields;
                      }
                    | {
                          formInitializationId: string;
                          arrayId: string;
                          itemId: string;
                          formFields: BaseFields;
                      }
                >
            ) => {
                const { formInitializationId, arrayId, formFields } = action.payload;
                const form = state.forms[formInitializationId];

                if (!form) {
                    return;
                }

                const arrayFieldState = form.fieldsState[arrayId];

                if (!arrayFieldState || !('order' in arrayFieldState)) {
                    return;
                }

                const removeIndex =
                    'removeIndex' in action.payload
                        ? action.payload.removeIndex
                        : arrayFieldState.order.indexOf(action.payload.itemId);

                const arrayValue = get<unknown[]>(
                    form.value,
                    getFieldPathById(() => form?.fieldsState ?? {}, arrayId)
                );
                arrayValue?.splice(removeIndex, 1);

                const deletedItemId = arrayFieldState.order[removeIndex];
                if (!deletedItemId) return;
                arrayFieldState.order.splice(removeIndex, 1);

                // Удаляем стейт и контекст удаленного поля и всех дочерних
                removeFieldAndChildrenStateAndContext(form, deletedItemId);

                setParentFieldsModifiedAt({ form, fieldId: arrayId, timestamp: Date.now(), visitedNodes: new Set() });
                setParentsValueByKey({
                    form,
                    formFields,
                    fieldId: arrayId,
                });
                setParentsStateByKey({
                    form,
                    formFields,
                    fieldId: arrayId,
                });
            },

            setStartedReactions: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    form?: StartedReaction[];
                    fields?: Dictionary<StartedReaction[]>;
                }>
            ) => {
                const formState = state.forms[action.payload.formInitializationId];

                if (!formState) {
                    return;
                }

                if (action.payload.form) {
                    formState.reactions.form = action.payload.form;
                }

                if (action.payload.fields) {
                    Object.assign(formState.reactions.fields, action.payload.fields);
                }
            },

            setAreReactionsStarted: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    value: boolean;
                }>
            ) => {
                const formState = state.forms[action.payload.formInitializationId];

                if (formState) {
                    formState.reactions.areReactionsStarted = action.payload.value;
                }
            },

            updateFormAndFieldsContext: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    fields: BaseFields;
                    fieldsContextArgs: Dictionary<unknown>;
                    formContext: unknown;
                }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];
                if (!form) {
                    return;
                }

                form.context.formContext = action.payload.formContext;
                form.context.fieldsContextArgs = action.payload.fieldsContextArgs;
                const newContext = getFieldsContext({
                    fields: action.payload.fields,
                    fieldsContextArgs: action.payload.fieldsContextArgs,
                    formValue: form.value,
                    fieldsState: form.fieldsState,
                });

                updateFieldsContext(form.context.fieldsContext, newContext);
                form.context.fieldsContextArgs = action.payload.fieldsContextArgs;
            },

            updateFieldAsyncActionsMeta: (
                state,
                action: PayloadAction<{
                    formInitializationId: string;
                    fieldId: string;
                    asyncActionsMeta: AsyncActionsMeta;
                }>
            ) => {
                const form = state.forms[action.payload.formInitializationId];
                if (!form) {
                    return;
                }

                const fieldState = form.fieldsState[action.payload.fieldId];
                if (!fieldState) {
                    return;
                }

                fieldState.asyncActionsMeta = action.payload.asyncActionsMeta;
            },
        },
    });

    const [useFormValue, getFormValue] = slice.createSelector(
        (state, formInitializationId: string) => state.forms[formInitializationId]?.value
    );
    const [, getFormInitialValue] = slice.createSelector(
        (state, formInitializationId: string) => state.forms[formInitializationId]?.state.initialValue
    );

    const [useIsFormDirty, isFormDirty] = slice.createSelector(
        (_, formInitializationId) =>
            !deepEqual(getFormValue(formInitializationId), getFormInitialValue(formInitializationId))
    );
    const [useIsFormInitialized, isFormInitialized] = slice.createSelector((state, formInitializationId: string) => {
        return Boolean(state.forms[formInitializationId]);
    });

    const [useIsAllFieldsValid, isAllFieldsValid] = slice.createSelector((_, formInitializationId: string) =>
        Object.values(getFieldsState(formInitializationId) ?? {}).every(fieldState => isValid(fieldState.errorsMap))
    );

    const [useFormStateRaw, getFormStateRaw] = slice.createSelector(
        (state, formInitializationId: string) => state.forms[formInitializationId]?.state
    );

    const [, getFormState] = slice.createSelector(
        (_, formInitializationId: string): Optional<FormState<unknown, BaseValidationError>> => {
            const formState = getFormStateRaw(formInitializationId);

            if (!formState) {
                return;
            }

            const isFieldsValid = isAllFieldsValid(formInitializationId);
            const isFromSelfValid = isValid(formState.errorsMap);

            return {
                ...formState,
                isValid: isFromSelfValid && isFieldsValid,
                isDirty: isFormDirty(formInitializationId) ?? false,
                canSubmit: !formState.isSubmitting && isFromSelfValid && isFieldsValid,
                errors: [
                    ...formState.errorsMap.onChange,
                    ...formState.errorsMap.onBlur,
                    ...formState.errorsMap.onSubmit,
                ],
            };
        }
    );

    // Отдельно определяем хук для лучшей мемоизации
    const useFormState = (formInitializationId: string) => {
        const formState = useFormStateRaw(formInitializationId);

        const isFieldsValid = useIsAllFieldsValid(formInitializationId);
        const isDirty = useIsFormDirty(formInitializationId);

        return useMemo(() => {
            if (!formState) return;

            const isFromSelfValid = isValid(formState.errorsMap);

            return {
                ...formState,
                isValid: isFromSelfValid && isFieldsValid,
                isDirty: isDirty ?? false,
                canSubmit: !formState.isSubmitting && isFromSelfValid && isFieldsValid,
                errors: [
                    ...formState.errorsMap.onChange,
                    ...formState.errorsMap.onBlur,
                    ...formState.errorsMap.onSubmit,
                ],
            };
        }, [formState, isDirty, isFieldsValid]);
    };

    const [, getFormValidationMeta] = slice.createSelector(
        (state, formInitializationId: string) => state.forms[formInitializationId]?.formValidationMeta
    );

    const [useFieldValue, getFieldValue] = slice.createSelector(
        (state, formInitializationId: string, fieldId: string) => {
            const fieldsState = getFieldsState(formInitializationId);

            const form = state.forms[formInitializationId];
            if (!form) {
                return undefined;
            }

            return get(
                form.value,
                getFieldPathById(() => fieldsState ?? {}, fieldId)
            );
        }
    );

    const [, getFieldInitialValue] = slice.createSelector((state, formInitializationId: string, fieldId: string) => {
        const form = state.forms[formInitializationId];
        if (!form) {
            return undefined;
        }

        return form.fieldsState[fieldId]?.initialValue;
    });

    const [useIsFieldDirty, isFieldDirty] = slice.createSelector(
        (_, formInitializationId: string, fieldId: string) =>
            !deepEqual(
                getFieldValue(formInitializationId, fieldId),
                getFieldInitialValue(formInitializationId, fieldId)
            )
    );

    const [useIsFieldValid, isFieldValid] = slice.createSelector((_, formInitializationId: string, fieldId: string) => {
        const fieldsState = getFieldsState(formInitializationId);

        function areFieldAndChildrenValid(fieldId: string): boolean {
            const fieldState = fieldsState?.[fieldId];
            if (!fieldState) return false;

            // Если поле не валидно, то не проверяем дочерние
            if (!isValid(fieldState.errorsMap)) {
                return false;
            }

            if ('fields' in fieldState) {
                return values(fieldState.fields).every(nestedFieldId => areFieldAndChildrenValid(nestedFieldId));
            }

            if ('order' in fieldState) {
                return fieldState.order.every(arrayItemId => areFieldAndChildrenValid(arrayItemId));
            }

            return true;
        }

        return areFieldAndChildrenValid(fieldId);
    });

    const [useIsFieldValidating, isFieldValidating] = slice.createSelector(
        (_, formInitializationId: string, fieldId: string) => {
            const fieldsState = getFieldsState(formInitializationId);

            function areFieldAndChildrenValidating(fieldId: string): boolean {
                const fieldState = fieldsState?.[fieldId];
                if (!fieldState) return false;

                // Если поле в процессе валидации, то не проверяем дочерние
                if (isValidating(fieldState.validationMeta)) {
                    return true;
                }

                if ('fields' in fieldState) {
                    return values(fieldState.fields).some(nestedFieldId =>
                        areFieldAndChildrenValidating(nestedFieldId)
                    );
                }

                if ('order' in fieldState) {
                    return fieldState.order.some(arrayItemId => areFieldAndChildrenValidating(arrayItemId));
                }

                return false;
            }

            return areFieldAndChildrenValidating(fieldId);
        }
    );

    const [useIsUninitializing, isUninitializing] = slice.createSelector((state, formInitializationId: string) => {
        const form = state.forms[formInitializationId];

        if (!form) {
            return false;
        }

        return form?.isUninitializing ?? false;
    });

    const [useIsFieldActionsRunning, isFieldActionsRunning] = slice.createSelector(
        (_, formInitializationId: string, fieldId: string) => {
            const fieldsState = getFieldsState(formInitializationId);

            function areFieldAndChildrenActionsRunning(fieldId: string): boolean {
                const fieldState = fieldsState?.[fieldId];
                if (!fieldState) return false;

                // Если у поля выполняются экшены, то не проверяем дочерние
                if (isAsyncActionsRunning(fieldState.asyncActionsMeta)) {
                    return true;
                }

                if ('fields' in fieldState) {
                    return values(fieldState.fields).some(nestedFieldId =>
                        areFieldAndChildrenActionsRunning(nestedFieldId)
                    );
                }

                if ('order' in fieldState) {
                    return fieldState.order.some(arrayItemId => areFieldAndChildrenActionsRunning(arrayItemId));
                }

                return false;
            }

            return areFieldAndChildrenActionsRunning(fieldId);
        }
    );

    const [useFieldStateRaw, getFieldStateRaw] = slice.createSelector(
        (state, formInitializationId: string, fieldId: string) => {
            return state.forms[formInitializationId]?.fieldsState[fieldId] ?? getDefaultFieldState();
        }
    );

    const [, getFieldState] = slice.createSelector(
        (_, formInitializationId: string, fieldId: string): FieldState<unknown, BaseValidationError> => {
            const fieldState = getFieldStateRaw(formInitializationId, fieldId);

            return {
                ...fieldState,
                isDirty: isFieldDirty(formInitializationId, fieldId) ?? false,
                isValid: isFieldValid(formInitializationId, fieldId),
                isValidating: isFieldValidating(formInitializationId, fieldId),
                isActionsRunning: isFieldActionsRunning(formInitializationId, fieldId),
                errors: [
                    ...fieldState.errorsMap.onChange,
                    ...fieldState.errorsMap.onBlur,
                    ...fieldState.errorsMap.onSubmit,
                ],
            };
        }
    );

    // Отдельно определяем хук для лучшей мемоизации
    const useFieldState = (formInitializationId: string, fieldId: string) => {
        const fieldState = useFieldStateRaw(formInitializationId, fieldId);
        const isDirty = useIsFieldDirty(formInitializationId, fieldId);
        const isValid = useIsFieldValid(formInitializationId, fieldId);
        const isValidating = useIsFieldValidating(formInitializationId, fieldId);
        const isActionsRunning = useIsFieldActionsRunning(formInitializationId, fieldId);

        return useMemo(() => {
            if (!fieldState) {
                return;
            }

            return {
                ...fieldState,
                isDirty: isDirty ?? false,
                isValid: isValid,
                isValidating: isValidating,
                isActionsRunning: isActionsRunning,
                errors: [
                    ...fieldState.errorsMap.onChange,
                    ...fieldState.errorsMap.onBlur,
                    ...fieldState.errorsMap.onSubmit,
                ],
            };
        }, [fieldState, isDirty, isValid, isValidating, isActionsRunning]);
    };

    const [, getFieldsState] = slice.createSelector(
        (state, formInitializationId: string) => state.forms[formInitializationId]?.fieldsState
    );

    const [, getSubscriptions] = slice.createSelector(
        (state, formInitializationId: string) => state.forms[formInitializationId]?.subscriptions
    );

    const [, getAreReactionsStarted] = slice.createSelector((state, formInitializationId: string) =>
        Boolean(state.forms[formInitializationId]?.reactions.areReactionsStarted)
    );

    const [, getStartedReactions] = slice.createSelector((state, formInitializationId: string) => ({
        form: state.forms[formInitializationId]?.reactions.form,
        fields: state.forms[formInitializationId]?.reactions.fields,
    }));

    const [, getArrayItemIdByIndex] = slice.createSelector(
        (state, options: { formInitializationId: string; arrayFieldId: string; index: number }) => {
            const fieldState = state.forms[options.formInitializationId]?.fieldsState[options.arrayFieldId];

            if (fieldState && 'order' in fieldState) {
                return fieldState.order[options.index];
            }

            return undefined;
        }
    );

    const [, getValueByKey] = slice.createSelector(
        (state, options: { formInitializationId: string; unionFieldId: string; unionKey: Primitive }) => {
            const fieldState = state.forms[options.formInitializationId]?.fieldsState[options.unionFieldId];

            if (fieldState && 'valuesByKey' in fieldState) {
                return fieldState.valuesByKey[String(options.unionKey)];
            }

            return undefined;
        }
    );

    const [, getStateByKey] = slice.createSelector(
        (state, options: { formInitializationId: string; unionFieldId: string; unionKey: Primitive }) => {
            const fieldState = state.forms[options.formInitializationId]?.fieldsState[options.unionFieldId];

            if (fieldState && 'statesByKey' in fieldState) {
                return fieldState.statesByKey[String(options.unionKey)];
            }

            return undefined;
        }
    );

    const [useFormContext, getFormContext] = slice.createSelector(
        (state, formInitializationId: string) => state.forms[formInitializationId]?.context.formContext
    );
    const [useFieldContext, getFieldContext] = slice.createSelector(
        (state, formInitializationId: string, fieldId: string) =>
            state.forms[formInitializationId]?.context.fieldsContext[fieldId]
    );
    const [, selectFieldsContext] = slice.createSelector(
        (state, formInitializationId: string) => state.forms[formInitializationId]?.context.fieldsContext
    );

    // Селектор для маппинга formId -> formInitializationId (композитный ключ)
    const [useFormInitializationId, getFormInitializationId] = slice.createSelector(
        (state, formId: string) => state.formIdToInitializationId[formId]
    );

    type FormParams = { formInitializationId: string };
    type FieldParams = { fieldId: string };

    return {
        getFormInitializationId: ({ formId }: { formId: string }) => getFormInitializationId(formId),
        useFormInitializationId: ({ formId }: { formId: string }) => useFormInitializationId(formId),
        initializeForm: slice.initializeForm,
        cleanupForm: slice.cleanupForm,
        resetForm: slice.resetForm,
        reinitializeForm: slice.reinitializeForm,
        setFormValue: slice.setFormValue,
        setFieldValue: slice.setFieldValue,
        setFieldValueAndResetState: slice.setFieldValueAndResetState,
        setValidationErrors: slice.setValidationErrors,
        addFormValidationErrors: slice.addFormValidationErrors,
        addFieldValidationErrors: slice.addFieldValidationErrors,
        addSubmitErrors: slice.addSubmitErrors,
        subscribe: slice.subscribe,
        unsubscribe: slice.unsubscribe,
        updateFormState: slice.updateFormState,
        getArrayItemIdByIndex: getArrayItemIdByIndex,
        updateValidationMeta: slice.updateValidationMeta,
        setStartedReactions: slice.setStartedReactions,
        getValueByKey: getValueByKey,
        getStateByKey: getStateByKey,
        getSubscriptions: ({ formInitializationId }: FormParams) => getSubscriptions(formInitializationId),
        getStartedReactions: ({ formInitializationId }: FormParams) => getStartedReactions(formInitializationId),
        getAreReactionsStarted: ({ formInitializationId }: FormParams) => getAreReactionsStarted(formInitializationId),
        setAreReactionsStarted: slice.setAreReactionsStarted,
        isFormInitialized: ({ formInitializationId }: FormParams) => isFormInitialized(formInitializationId),
        useIsFormInitialized: ({ formInitializationId }: FormParams) => useIsFormInitialized(formInitializationId),
        getFormValue: ({ formInitializationId }: FormParams) => getFormValue(formInitializationId),
        useFormValue: ({ formInitializationId }: FormParams) => useFormValue(formInitializationId),
        getFormState: ({ formInitializationId }: FormParams) => getFormState(formInitializationId),
        useFormState: ({ formInitializationId }: FormParams) => useFormState(formInitializationId),
        getFormContext: ({ formInitializationId }: FormParams) => getFormContext(formInitializationId),
        useFormContext: ({ formInitializationId }: FormParams) => useFormContext(formInitializationId),
        getFormValidationMeta: ({ formInitializationId }: FormParams) => getFormValidationMeta(formInitializationId),
        updateFieldAsyncActionsMeta: slice.updateFieldAsyncActionsMeta,
        getFieldValue: ({ formInitializationId, fieldId }: FormParams & FieldParams) =>
            getFieldValue(formInitializationId, fieldId),
        useFieldValue: ({ formInitializationId, fieldId }: FormParams & FieldParams) =>
            useFieldValue(formInitializationId, fieldId),
        getFieldState: ({ formInitializationId, fieldId }: FormParams & FieldParams) =>
            getFieldState(formInitializationId, fieldId),
        useFieldState: ({ formInitializationId, fieldId }: FormParams & FieldParams) =>
            useFieldState(formInitializationId, fieldId),
        getFieldsState: ({ formInitializationId }: FormParams) => getFieldsState(formInitializationId),
        getFieldContext: ({ formInitializationId, fieldId }: FormParams & FieldParams) =>
            getFieldContext(formInitializationId, fieldId),
        useFieldContext: ({ formInitializationId, fieldId }: FormParams & FieldParams) =>
            useFieldContext(formInitializationId, fieldId),
        getFieldsContext: ({ formInitializationId }: FormParams) => selectFieldsContext(formInitializationId),
        touch: slice.touch,
        updateFormAndFieldsContext: slice.updateFormAndFieldsContext,
        insertArrayItem: slice.insertArrayItems,
        removeArrayItem: slice.removeArrayItems,
        createSelector: slice.createSelector,
        setParentsStateByKey: slice.setParentsStateByKey,
        useIsUninitializing: ({ formInitializationId }: FormParams) => useIsUninitializing(formInitializationId),
        getIsUninitializing: ({ formInitializationId }: FormParams) => isUninitializing(formInitializationId),
        setIsUninitializing: slice.setIsUninitializing,
    } satisfies FormStore;
}

export function getFormStoreSingleton(name: string): FormStore {
    if (!storeManager.has(name)) {
        storeManager.set(name, createFormStore(name));
    }

    // Get возвращает Optional значение, но тут оно точно есть
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    return storeManager.get(name) as FormStore;
}
